import { logger } from "@/shared/logger";

/**
 * Client-side JWT session window. We treat the session as ended slightly before
 * the JWT `exp` so users re-authenticate instead of hitting 401 on API calls.
 */
export const JWT_SESSION_END_BUFFER_SECONDS = 3600;

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    return JSON.parse(atob(parts[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Returns JWT `exp` in seconds since epoch, or null if missing or unreadable.
 */
export function getJwtExpSeconds(token: string): number | null {
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  const exp = payload.exp;
  return typeof exp === "number" ? exp : null;
}

/**
 * True when the app should stop using this token (logout / omit Authorization).
 * Uses {@link JWT_SESSION_END_BUFFER_SECONDS} before `exp`.
 * Malformed tokens are treated as ended. Tokens without `exp` are treated as not ended.
 */
export function isJwtSessionEnded(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload) return true;
  const exp = payload.exp;
  if (typeof exp !== "number") return false;
  const now = Math.floor(Date.now() / 1000);
  const ended = now >= exp - JWT_SESSION_END_BUFFER_SECONDS;
  logger.debug("jwt.isSessionEnded", {
    ended,
    now,
    exp,
    bufferSeconds: JWT_SESSION_END_BUFFER_SECONDS,
  });
  return ended;
}

/**
 * Milliseconds until the client session end (JWT exp minus buffer). Null if no exp.
 */
export function getMsUntilJwtSessionEnd(token: string): number | null {
  const exp = getJwtExpSeconds(token);
  if (exp == null) return null;
  const endMs = (exp - JWT_SESSION_END_BUFFER_SECONDS) * 1000;
  return endMs - Date.now();
}
